import { Box, Typography } from "@mui/material";
import type { TFunction } from "i18next";
import { DateTime } from "luxon";
import type { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import EmployeeStatusChip from "../../../../components/UIToolkit/Badges/EmployeeStatusChip";
import MetaChip from "../../../../components/UIToolkit/Badges/MetaChip";
import { type CompareRow, type CompareSide, SideBySideCompare } from "../../../../components/UIToolkit/Compare/SideBySideCompare";
import { useLastNameFirst } from "../../../../hooks/useNameFormat";
import { ALPHA, FONT_SIZE, INK, MOTION } from "../../../../theme/tokens";
import { formatPersonFullName } from "../../../../utils/formatters";
import type { MergeNurseSummary, MergeWageHistoryEntry, NurseMergePreview } from "../../../../utils/nurseMergeModels";
import { employmentTypeKey, membershipStatus } from "./mergeLogic";
import type { MergeLookups } from "./useMergeDuplicateFlow";

type Props = {
	preview: NurseMergePreview;
	/** Left-to-right order — the profile's own record first, so the columns never swap under the cursor. */
	orderedNurseIds: [number, number];
	keepNurseId: number;
	onKeepChange: (nurseId: number) => void;
	/** The preview is being rebuilt for a new choice — the comparison dims in place instead of disappearing. */
	isRefreshing: boolean;
	lookups: MergeLookups;
	/** Wage periods are displayed in the facility timezone. */
	timezone: string;
};

/** Step 2: the two records side by side, and which one survives. */
export const MergeCompareStep: FC<Props> = ({ preview, orderedNurseIds, keepNurseId, onKeepChange, isRefreshing, lookups, timezone }) => {
	const { t, i18n } = useTranslation();
	const lastNameFirst = useLastNameFirst();
	const byId = new Map([preview.primary, preview.duplicate].map((summary) => [summary.nurseId, summary]));
	const summaries = orderedNurseIds.map((id) => byId.get(id)).filter((summary): summary is MergeNurseSummary => summary !== undefined);

	const sides: CompareSide<number>[] = summaries.map((summary) => ({
		id: summary.nurseId,
		title: formatPersonFullName(summary, lastNameFirst),
		subtitle: `#${summary.nurseId}`,
		badge:
			summary.nurseId === keepNurseId ? (
				<MetaChip label={t("nurse.merge.compare.badge.kept")} tone="success" />
			) : (
				<MetaChip label={t("nurse.merge.compare.badge.deleted")} tone="error" />
			),
		dataTestId: `merge-employees-keep-${summary.nurseId}`,
	}));

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }} data-testid="merge-employees-compare-step">
			<Typography sx={{ fontSize: FONT_SIZE.EMPHASIS, color: INK.SECONDARY, lineHeight: 1.5 }}>{t("nurse.merge.compare.intro")}</Typography>
			{/* Dimmed in place, not an inert overlay: the swap is started from these radios, and an inert region
			    would throw keyboard focus off the one the person just moved to. */}
			<Box
				aria-busy={isRefreshing}
				data-testid="merge-employees-compare-busy"
				sx={{
					opacity: isRefreshing ? ALPHA.MUTED_SECONDARY : 1,
					transition: `opacity ${MOTION.DURATION.OVERLAY_FADE}ms ${MOTION.EASE_OUT}`,
					"@media (prefers-reduced-motion: reduce)": { transition: "none" },
				}}
			>
				<SideBySideCompare<number>
					sides={sides}
					rows={buildRows(t, summaries, lookups, timezone, i18n.language, lastNameFirst)}
					selectedSideId={keepNurseId}
					onSelectSide={onKeepChange}
					selectionAriaLabel={t("nurse.merge.compare.keep.aria")}
					ariaLabel={t("nurse.merge.compare.table.aria")}
					differenceLabel={t("nurse.merge.compare.differs")}
					renderDifferenceSummary={(count) => t("nurse.merge.compare.differs.summary", { count })}
					dataTestId="merge-employees-compare"
				/>
			</Box>
		</Box>
	);
};

const byNurse = <T,>(summaries: MergeNurseSummary[], read: (summary: MergeNurseSummary) => T): Record<number, T> =>
	Object.fromEntries(summaries.map((summary) => [summary.nurseId, read(summary)]));

const Muted: FC<{ children: ReactNode }> = ({ children }) => (
	<Box component="span" sx={{ color: INK.MUTED, fontWeight: 400 }}>
		{children}
	</Box>
);

const Stack: FC<{ children: ReactNode }> = ({ children }) => (
	<Box component="span" sx={{ display: "flex", flexDirection: "column", gap: 0.75, minWidth: 0 }}>
		{children}
	</Box>
);

const buildRows = (
	t: TFunction,
	summaries: MergeNurseSummary[],
	lookups: MergeLookups,
	timezone: string,
	language: string,
	lastNameFirst: boolean,
): CompareRow<number>[] => {
	const notSet = <Muted>{t("nurse.merge.compare.not.set")}</Muted>;
	// DEFAULT: an unset field compares as empty text, so two unset values read as equal rather than different.
	const text = (value: string | null) => value ?? "";
	const jobNames = (summary: MergeNurseSummary) => summary.jobIds.map(lookups.jobName);
	const departmentKey = (summary: MergeNurseSummary) =>
		summary.memberships
			.map((membership) => `${membership.departmentId}:${membershipStatus(membership)}`)
			.sort()
			.join("|");
	const linkLabel = (link: MergeNurseSummary["externalLinks"][number]) =>
		link.departmentId ? `${link.source} · ${link.externalId} · ${lookups.departmentName(link.departmentId)}` : `${link.source} · ${link.externalId}`;

	return [
		{
			id: "name",
			label: t("nurse.merge.field.name"),
			values: byNurse(summaries, (summary) => formatPersonFullName(summary, lastNameFirst)),
			compareValues: byNurse(summaries, (summary) => formatPersonFullName(summary, lastNameFirst)),
		},
		{ id: "id", label: t("nurse.merge.field.id"), values: byNurse(summaries, (summary) => `#${summary.nurseId}`) },
		{
			id: "role",
			label: t("nurse.merge.field.role"),
			// DEFAULT: an unset field shows "Not set" — a visible value, not an empty cell that reads as missing data.
			values: byNurse(summaries, (summary) => summary.role ?? notSet),
			compareValues: byNurse(summaries, (summary) => text(summary.role)),
		},
		{
			id: "jobs",
			label: t("nurse.merge.field.jobs"),
			values: byNurse(summaries, (summary) => (summary.jobIds.length ? jobNames(summary).join(", ") : notSet)),
			compareValues: byNurse(summaries, (summary) => jobNames(summary).sort().join(",")),
		},
		{
			id: "employment",
			label: t("nurse.merge.field.employment"),
			values: byNurse(summaries, (summary) => (summary.employmentType ? t(employmentTypeKey(summary.employmentType)) : notSet)),
			compareValues: byNurse(summaries, (summary) => text(summary.employmentType)),
		},
		{
			id: "phone",
			label: t("nurse.merge.field.phone"),
			// DEFAULT: "Not set" for a missing or blank phone, as above.
			values: byNurse(summaries, (summary) => summary.phoneNumber || notSet),
			compareValues: byNurse(summaries, (summary) => text(summary.phoneNumber)),
		},
		{
			id: "email",
			label: t("nurse.merge.field.email"),
			// DEFAULT: "Not set" for a missing or blank email, as above.
			values: byNurse(summaries, (summary) => summary.email || notSet),
			compareValues: byNurse(summaries, (summary) => text(summary.email)),
		},
		{
			id: "login",
			label: t("nurse.merge.field.login"),
			values: byNurse(summaries, (summary) => (summary.userId ? t("nurse.merge.login.has") : <Muted>{t("nurse.merge.login.none")}</Muted>)),
			compareValues: byNurse(summaries, (summary) => (summary.userId ? "login" : "")),
		},
		{
			id: "departments",
			label: t("nurse.merge.field.departments"),
			values: byNurse(summaries, (summary) =>
				summary.memberships.length === 0 ? (
					notSet
				) : (
					<Stack>
						{summary.memberships.map((membership) => {
							const status = membershipStatus(membership);
							return (
								<Box key={membership.departmentId} component="span" sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.75 }}>
									<span>{lookups.departmentName(membership.departmentId)}</span>
									{status && <EmployeeStatusChip status={status} />}
								</Box>
							);
						})}
					</Stack>
				),
			),
			compareValues: byNurse(summaries, departmentKey),
		},
		{
			id: "hris",
			label: t("nurse.merge.field.hris"),
			values: byNurse(summaries, (summary) =>
				summary.externalLinks.length === 0 ? (
					<Muted>{t("nurse.merge.compare.no.hris")}</Muted>
				) : (
					<Stack>
						{summary.externalLinks.map((link) => (
							<span key={`${link.source}-${link.externalId}-${link.syncKey}`}>{linkLabel(link)}</span>
						))}
					</Stack>
				),
			),
			compareValues: byNurse(summaries, (summary) => summary.externalLinks.map(linkLabel).sort().join("|")),
		},
		{
			id: "wage-history",
			label: t("nurse.merge.field.wage.history"),
			values: byNurse(summaries, (summary) =>
				summary.wageHistory.length === 0 ? (
					<Muted>{t("nurse.merge.compare.no.wage.history")}</Muted>
				) : (
					<Stack>
						{summary.wageHistory.map((entry) => (
							<Box key={entry.id} component="span" sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
								<Typography component="span" sx={{ fontSize: "inherit", fontWeight: 700, color: INK.PRIMARY }}>
									{formatWage(entry, language, t)}
								</Typography>
								<Typography component="span" sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.MUTED, fontVariantNumeric: "tabular-nums" }}>
									{formatWagePeriod(entry, timezone, language, t)}
								</Typography>
							</Box>
						))}
					</Stack>
				),
			),
			compareValues: byNurse(summaries, (summary) =>
				summary.wageHistory.map((entry) => `${entry.wage}:${entry.wageType}:${entry.startTime.toISO()}:${entry.endTime?.toISO()}`).join("|"),
			),
		},
	];
};

const WAGE_TYPE_KEYS: Readonly<Record<string, string>> = {
	Hourly: "nurse.merge.wage.type.hourly",
	Annually: "nurse.merge.wage.type.annually",
};

const formatWage = (entry: MergeWageHistoryEntry, language: string, t: TFunction): string => {
	const numericWage = parseWageAmount(entry);
	if (!Number.isFinite(numericWage)) throw new Error(`Invalid wage history amount for wage row ${entry.id}`);
	const wageTypeKey = entry.wageType === null ? "nurse.merge.wage.type.not.recorded" : WAGE_TYPE_KEYS[entry.wageType];
	if (wageTypeKey === undefined) throw new Error(`Invalid wage history type for wage row ${entry.id}`);
	const amount = new Intl.NumberFormat(language, { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
		numericWage,
	);
	return t("nurse.merge.wage.amount", { amount, type: t(wageTypeKey) });
};

const parseWageAmount = (entry: MergeWageHistoryEntry): number => {
	if (typeof entry.wage === "number") return entry.wage;
	const decimal = entry.wage.trim();
	if (!/^-?\d+(\.\d+)?$/.test(decimal)) throw new Error(`Invalid wage history amount for wage row ${entry.id}`);
	return Number(decimal);
};

const formatWagePeriod = (entry: MergeWageHistoryEntry, timezone: string, language: string, t: TFunction): string => {
	const start = entry.startTime.setZone(timezone).setLocale(language).toLocaleString(DateTime.DATE_MED);
	const end = entry.endTime === null ? t("nurse.merge.wage.present") : entry.endTime.setZone(timezone).setLocale(language).toLocaleString(DateTime.DATE_MED);
	return t("nurse.merge.wage.period", { start, end });
};
